import type { CSSProperties } from "react";

import { hexOf, FONT_STACKS, type ContactEntry, type ContactsStyle, type ContactDisplay } from "../domain/canvas-model";
import { CONTACT_ICONS, CONTACT_BRAND_COLORS, CONTACT_KIND_LABELS, contactHref } from "./contact-uris";
import { useContentAnchorProps } from "./contentPointer";

/**
 * Shared CONTACTS block renderer used by BOTH the private Canvas editor and the
 * public Grid publication layer (pixel-identical). Sizes scale by camera scale.
 * Per-entry display can override the block default: ICON, ICON_LABEL (icon +
 * label), ICON_DATA (icon + the datum), BUTTON (filled chip). Presets seed
 * defaults; every field is authored explicitly and wins.
 *
 * `interactive` = true only on the public Grid: rows become real links (mailto:/
 * tel:/wa.me/https/maps) with pointer stop-propagation and hover motion.
 */
export function ContactsRender({
  entries, style, scale, interactive,
}: {
  entries: ContactEntry[]; style: ContactsStyle; scale: number; interactive: boolean;
}) {
  const anchorProps = useContentAnchorProps();
  const px = (n: number) => Math.max(0, n * scale);
  const s = style;
  const iconSize = Math.max(4, px(s.icon_size));
  const fontSize = Math.max(1, px(s.font_size));
  const gap = px(s.gap);
  const pad = px(s.padding);
  const radius = s.radius === "PILL" ? 9999 : s.radius === "ROUNDED" ? 12 * scale : s.radius === "SLIGHT" ? 5 * scale : 0;
  const labelColor = hexOf(s.label_color, "#f5f7fa");
  const dataColor = hexOf(s.data_color, "#c8d0dc");
  const labelFont = FONT_STACKS[s.font] ?? FONT_STACKS.GRID_SANS;
  const justify = s.align === "START" ? "flex-start" : s.align === "END" ? "flex-end" : "center";

  const container: CSSProperties = s.direction === "GRID"
    ? { display: "inline-grid", gridTemplateColumns: `repeat(${Math.max(1, s.columns)}, minmax(0, max-content))`, gap, justifyContent: justify, alignContent: "center", placeItems: "center", boxSizing: "border-box" }
    : { display: "inline-flex", flexDirection: s.direction === "HORIZONTAL" ? "row" : "column", flexWrap: "nowrap", gap, alignItems: s.direction === "VERTICAL" ? (justify as CSSProperties["alignItems"]) : "center", justifyContent: justify, boxSizing: "border-box" };

  return (
    <div style={container}>
      {entries.map((e, i) => {
        const disp: ContactDisplay = e.display ?? s.display;
        const Icon = CONTACT_ICONS[e.kind] ?? CONTACT_ICONS.WEBSITE;
        const iconColor = e.icon_color
          ? hexOf(e.icon_color)
          : s.use_brand_colors ? (CONTACT_BRAND_COLORS[e.kind] ?? "#f5f7fa") : hexOf(s.icon_color, "#f5f7fa");
        const label = (e.label && e.label.trim()) || CONTACT_KIND_LABELS[e.kind] || e.kind;
        const isButton = disp === "BUTTON";
        const showLabel = disp === "ICON_LABEL" || isButton;
        const showData = disp === "ICON_DATA";
        const chrome = isButton || s.bg_enabled || s.border_enabled;
        const bg = (isButton || s.bg_enabled) ? hexOf(s.bg_color, "#2b3340") : "transparent";
        const border = s.border_enabled ? `${Math.max(1, 1.5 * scale)}px solid ${hexOf(s.border_color, "#8a97a8")}` : "none";
        const item = (
          <span
            className={interactive && s.hover !== "NONE" ? `social-item hv-${s.hover.toLowerCase()}` : "social-item"}
            style={{
              display: "inline-flex", flexDirection: "row", alignItems: "center", gap: px(8),
              background: bg, border, borderRadius: radius, padding: chrome ? `${px(8)}px ${pad}px` : 0,
              color: iconColor, boxSizing: "border-box", maxWidth: "100%", textDecoration: "none",
            }}
          >
            <Icon size={iconSize} color={iconColor} style={{ flexShrink: 0 }} />
            {showLabel && (
              <span style={{ fontFamily: labelFont, fontSize, color: labelColor, lineHeight: 1.15, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>
            )}
            {showData && (
              <span style={{ fontFamily: labelFont, fontSize, color: dataColor, lineHeight: 1.15, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{e.value}</span>
            )}
          </span>
        );
        const href = contactHref(e.kind, e.value);
        if (interactive && href) {
          const external = href.startsWith("https://");
          return (
            <a key={i} data-testid="grid-pub-contact-link" href={href}
              target={external ? "_blank" : undefined} rel={external ? "noopener noreferrer" : undefined}
              {...anchorProps}
              style={{ display: "inline-flex", pointerEvents: "auto", textDecoration: "none", cursor: "pointer" }}>
              {item}
            </a>
          );
        }
        return <span key={i} style={{ display: "inline-flex" }}>{item}</span>;
      })}
    </div>
  );
}
